"use client";

import PropertyCard from "@/components/frontend/marketsuite/property/card";
import AnchorLink from "@/components/frontend/ui/anchor-link";
import { Button } from "@/components/frontend/ui/button";
import { useFavorites } from "@/contexts/frontend/favorites-context";
import type { Property } from "@/lib/types/marketsuite/datatypes/property";
import { useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import type { ComponentProps } from "react";

interface FavoritesOverviewClientProps {
	groups: { propertyType: string; urlBase?: string }[];
	toggleGroupClassName: string;
	archiveButtonVariant: ComponentProps<typeof Button>["variant"];
}

export function FavoritesOverviewClient({ groups, toggleGroupClassName, archiveButtonVariant }: FavoritesOverviewClientProps) {
	const t = useTranslations("ms");
	const locale = useLocale();
	const { favorites } = useFavorites();
	const favoriteIds = [...new Set(groups.flatMap(({ propertyType }) => favorites[propertyType] ?? []))];

	const { data } = useQuery({
		queryKey: ["favorite-properties", favoriteIds],
		enabled: favoriteIds.length > 0,
		queryFn: async () => {
			const params = new URLSearchParams({ perPage: favoriteIds.length.toString(), sort: "-created_at" });
			for (const id of favoriteIds) params.append("id", id);

			const response = await fetch(`/${locale}/next/property-overview?${params.toString()}`);
			if (!response.ok) throw new Error("Failed to fetch favorite properties");

			return (await response.json()) as { data: Partial<Property>[] };
		},
	});

	const propertiesById = new Map((data?.data ?? []).map((property) => [property.id, property]));
	const resolvedGroups = groups.map((group) => ({
		...group,
		properties: (favorites[group.propertyType] ?? []).map((id) => propertiesById.get(id)).filter((property) => property !== undefined),
	}));

	return (
		<div className="flex flex-col">
			{resolvedGroups.reduce((total, group) => total + group.properties.length, 0) >= 8 && (
				<nav className={`${toggleGroupClassName} mb-8`} aria-label={t("favorites.nav-aria-label")}>
					<div className="ms-toggle-group p-1 gap-1 flex border overflow-auto">
						{resolvedGroups.map(({ propertyType }) => (
							<AnchorLink
								key={propertyType}
								href={`#favorites-${propertyType}`}
								className="ms-toggle whitespace-nowrap flex items-center justify-center select-none focus-visible:outline-2 focus-visible:-outline-offset-1 no-underline text-inherit"
							>
								{t(`property-types.${propertyType}`)}
							</AnchorLink>
						))}
					</div>
				</nav>
			)}

			<div className="flex flex-col gap-12">
				{resolvedGroups.map(({ propertyType, properties, urlBase }) =>
					properties.length > 0 ? (
						<section key={propertyType} id={`favorites-${propertyType}`} className="flex flex-col gap-2">
							<h3>
								{t(`property-types.${propertyType}`)} ({properties.length})
							</h3>

							<ol className="grid gap-x-4 gap-y-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 mt-4">
								{properties.map((property, index) => (
									<li key={property.id ?? `${propertyType}-${index}`}>
										<PropertyCard urlBase={urlBase ?? ""} variant="default" property={property} />
									</li>
								))}
							</ol>

							{urlBase && (
								<Button href={urlBase} variant={archiveButtonVariant} className="mt-4 w-fit">
									{t("favorites.view-all-objects")}
								</Button>
							)}
						</section>
					) : null,
				)}
			</div>
		</div>
	);
}
